/** PAS Practice library item — append-only; expire by study count only (no calendar TTL). */

export type PasPracticeItemStatus = 'active' | 'expired';

export type PasPracticeLibraryItem = {
  id: string;
  question: string;
  answer: string;
  notes?: string;
  createdAt: string;
  /** Times the learner practiced this item */
  studyCount: number;
  status: PasPracticeItemStatus;
  /**
   * Legacy field kept for persistence compat. Not used for expiry —
   * items expire only when studyCount >= maxStudyCount.
   */
  expiresAt: string;
  /** Soft study budget — studyCount >= this → expired (item kept) */
  maxStudyCount: number;
  /** Marked as bad sample — kept in list (red) and fed to generator as counterexample. */
  negativeExample?: boolean;
  pathId?: string;
  transitionId?: string;
  entranceKind?: string;
  practiceKind?: 'entrance' | 'transition' | 'obstacle';
  implicitAbilityTagId?: string;
  treeRole?: 'root' | 'internal' | 'leaf';
  eligibleForStream?: boolean;
  reuseSource?: string;
  /**
   * Ladder proximity to original problem (1–5).
   * 5 = closest/hardest; 1 = furthest/easiest.
   */
  proximityLevel?: number;
};

export const DEFAULT_PRACTICE_MAX_STUDY_COUNT = 3;

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function asFiniteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** @deprecated No calendar TTL — kept for older persisted payloads. */
export function computePasPracticeExpiresAt(createdAtIso: string): string {
  const created = Date.parse(createdAtIso);
  const base = Number.isFinite(created) ? created : Date.now();
  return new Date(base + 100 * 365 * 24 * 60 * 60 * 1000).toISOString();
}

export function refreshPasPracticeItemStatus(
  item: PasPracticeLibraryItem,
): PasPracticeLibraryItem {
  const maxStudy = Math.max(1, Number(item.maxStudyCount) || DEFAULT_PRACTICE_MAX_STUDY_COUNT);
  const expiredByStudy = item.studyCount >= maxStudy;
  const nextStatus: PasPracticeItemStatus = expiredByStudy ? 'expired' : 'active';
  if (nextStatus === item.status) return item;
  return { ...item, status: nextStatus };
}

export function refreshPasPracticeLibrary(
  items: PasPracticeLibraryItem[],
): PasPracticeLibraryItem[] {
  return items.map((item) => refreshPasPracticeItemStatus(item));
}

/** Valid for target count — excludes study-full and negative examples. */
export function countActivePasPracticeItems(items: PasPracticeLibraryItem[]): number {
  return items.filter((item) => {
    const refreshed = refreshPasPracticeItemStatus(item);
    return refreshed.status === 'active' && !refreshed.negativeExample;
  }).length;
}

export function listPasPracticeNegativeExampleQuestions(
  items: PasPracticeLibraryItem[],
): string[] {
  return items
    .filter((item) => item.negativeExample)
    .map((item) => String(item.question || '').trim())
    .filter(Boolean);
}

export function togglePasPracticeNegativeExample(
  item: PasPracticeLibraryItem,
): PasPracticeLibraryItem {
  return refreshPasPracticeItemStatus({
    ...item,
    negativeExample: !item.negativeExample,
  });
}

export function createPasPracticeLibraryItem(input: {
  id: string;
  question: string;
  answer: string;
  notes?: string;
  createdAt?: string;
  maxStudyCount?: number;
  negativeExample?: boolean;
  pathId?: string;
  transitionId?: string;
  entranceKind?: string;
  practiceKind?: 'entrance' | 'transition' | 'obstacle';
  implicitAbilityTagId?: string;
  treeRole?: 'root' | 'internal' | 'leaf';
  eligibleForStream?: boolean;
  reuseSource?: string;
  proximityLevel?: number;
}): PasPracticeLibraryItem {
  const createdAt = input.createdAt || new Date().toISOString();
  const maxStudyCount = Math.max(1, input.maxStudyCount ?? DEFAULT_PRACTICE_MAX_STUDY_COUNT);
  const proximity = asFiniteNumber(input.proximityLevel);
  return {
    id: input.id,
    question: input.question,
    answer: input.answer,
    notes: input.notes,
    createdAt,
    studyCount: 0,
    status: 'active',
    expiresAt: computePasPracticeExpiresAt(createdAt),
    maxStudyCount,
    negativeExample: input.negativeExample === true ? true : undefined,
    pathId: input.pathId,
    transitionId: input.transitionId,
    entranceKind: input.entranceKind,
    practiceKind: input.practiceKind,
    implicitAbilityTagId: input.implicitAbilityTagId,
    treeRole: input.treeRole,
    eligibleForStream: input.eligibleForStream,
    reuseSource: input.reuseSource,
    proximityLevel: proximity != null
      ? Math.max(1, Math.min(5, Math.round(proximity)))
      : undefined,
  };
}

export function recordPasPracticeStudy(
  item: PasPracticeLibraryItem,
): PasPracticeLibraryItem {
  const studyCount = Math.max(0, item.studyCount) + 1;
  return refreshPasPracticeItemStatus({
    ...item,
    studyCount,
  });
}

export function parsePasPracticeLibraryItem(raw: unknown): PasPracticeLibraryItem | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const id = asNonEmptyString(record.id);
  const question = asNonEmptyString(record.question);
  const answer = asNonEmptyString(record.answer) || '';
  if (!id || !question) return null;
  const createdAt = asNonEmptyString(record.createdAt) || new Date().toISOString();
  const studyCount = Math.max(0, asFiniteNumber(record.studyCount) ?? 0);
  const maxStudyCount = Math.max(
    1,
    asFiniteNumber(record.maxStudyCount) ?? DEFAULT_PRACTICE_MAX_STUDY_COUNT,
  );
  const expiresAt = asNonEmptyString(record.expiresAt)
    || computePasPracticeExpiresAt(createdAt);
  const statusRaw = asNonEmptyString(record.status);
  const status: PasPracticeItemStatus = statusRaw === 'expired' ? 'expired' : 'active';
  const negativeExample = record.negativeExample === true;
  return refreshPasPracticeItemStatus({
    id,
    question,
    answer,
    notes: asNonEmptyString(record.notes) || undefined,
    createdAt,
    studyCount,
    status,
    expiresAt,
    maxStudyCount,
    negativeExample: negativeExample ? true : undefined,
    pathId: asNonEmptyString(record.pathId) || undefined,
    transitionId: asNonEmptyString(record.transitionId) || undefined,
    entranceKind: asNonEmptyString(record.entranceKind) || undefined,
    practiceKind: record.practiceKind === 'entrance'
      || record.practiceKind === 'transition'
      || record.practiceKind === 'obstacle'
      ? record.practiceKind
      : undefined,
    implicitAbilityTagId: asNonEmptyString(record.implicitAbilityTagId) || undefined,
    treeRole: record.treeRole === 'root' || record.treeRole === 'internal' || record.treeRole === 'leaf'
      ? record.treeRole
      : undefined,
    eligibleForStream: record.eligibleForStream === false ? false : undefined,
    reuseSource: asNonEmptyString(record.reuseSource) || undefined,
    proximityLevel: (() => {
      const n = asFiniteNumber(record.proximityLevel);
      if (n == null) return undefined;
      return Math.max(1, Math.min(5, Math.round(n)));
    })(),
  });
}

export function parsePasPracticeLibrary(raw: unknown): PasPracticeLibraryItem[] {
  if (!Array.isArray(raw)) return [];
  const out: PasPracticeLibraryItem[] = [];
  for (const item of raw) {
    const parsed = parsePasPracticeLibraryItem(item);
    if (parsed) out.push(parsed);
  }
  return out;
}

export function normalizePasPracticeQuestion(question?: string | null): string {
  return String(question || '').replace(/\s+/g, ' ').trim();
}

export function pasPracticeLibraryHasLeaf(
  items: PasPracticeLibraryItem[],
  leaf: { id?: string | null; question?: string | null },
): boolean {
  const id = String(leaf.id || '').trim();
  const question = normalizePasPracticeQuestion(leaf.question);
  return items.some((item) => (
    (id && item.id === id)
    || (question && normalizePasPracticeQuestion(item.question) === question)
  ));
}

export function mergePasPracticeLibraryItems(
  prev: PasPracticeLibraryItem[],
  nextItems: PasPracticeLibraryItem[],
): PasPracticeLibraryItem[] {
  const seenIds = new Set(prev.map((item) => item.id));
  const seenQuestions = new Set(
    prev.map((item) => normalizePasPracticeQuestion(item.question)).filter(Boolean),
  );
  const merged = [...prev];
  for (const item of nextItems) {
    const question = normalizePasPracticeQuestion(item.question);
    if (seenIds.has(item.id) || (question && seenQuestions.has(question))) continue;
    seenIds.add(item.id);
    if (question) seenQuestions.add(question);
    merged.push(item);
  }
  return refreshPasPracticeLibrary(merged);
}
